import { MATURA_SESSIONS } from "./officialMaturaHistory";

export const MATURA_ANALYSIS_TARGETS = Object.freeze([30, 50, 60, 80, 100]);
export const MATURA_REALIZATION_SCENARIOS = Object.freeze([
  { id: "full", label: "pełna realizacja", ratio: 1 },
  { id: "expected", label: "realistycznie", ratio: 0.8 },
  { id: "conservative", label: "ostrożnie", ratio: 2 / 3 },
]);
export const DEFAULT_MATURA_SESSION_FILTER = Object.freeze([
  MATURA_SESSIONS.MAY, MATURA_SESSIONS.JUNE, MATURA_SESSIONS.AUGUST,
]);

export function filterOfficialMaturaSheets(sheets, sessions = DEFAULT_MATURA_SESSION_FILTER) {
  const allowed = new Set(sessions);
  return (Array.isArray(sheets) ? sheets : []).filter((sheet) => (
    sheet?.isOfficial === true && sheet?.isMock !== true && allowed.has(sheet.session)
  ));
}

export function analyzeMaturaTopicFrequency(sheets, sessions = DEFAULT_MATURA_SESSION_FILTER) {
  const selectedSheets = filterOfficialMaturaSheets(sheets, sessions);
  const byCode = new Map();

  selectedSheets.forEach((sheet) => {
    const sheetCodes = new Set();
    (sheet.slots || []).forEach((task) => {
      if (!task?.code3 || !Number.isFinite(task.points)) return;
      const topicPoints = Array.isArray(task.topicPoints) && task.topicPoints.length
        ? task.topicPoints
        : [{ code3: task.code3, points: task.points }];

      topicPoints.forEach((attribution) => {
        if (!attribution?.code3 || !Number.isFinite(attribution.points)) return;
        if (!byCode.has(attribution.code3)) {
          byCode.set(attribution.code3, { code3: attribution.code3, appearances: 0, totalPoints: 0, closedPoints: 0, openPoints: 0, tasks: 0, sources: [] });
        }
        const row = byCode.get(attribution.code3);
        row.totalPoints += attribution.points;
        row.tasks += 1;
        row.sources.push({
          sheetId: sheet.id,
          year: sheet.year,
          session: sheet.session,
          taskNo: task.taskNo,
          points: attribution.points,
          taskPoints: task.points,
        });
        if (task.kind === "closed") row.closedPoints += attribution.points;
        else row.openPoints += attribution.points;
        sheetCodes.add(attribution.code3);
      });
    });
    sheetCodes.forEach((code3) => { byCode.get(code3).appearances += 1; });
  });

  return Array.from(byCode.values()).map((row) => {
    const pointsBySheet = selectedSheets.map((sheet) => row.sources
      .filter((source) => source.sheetId === sheet.id)
      .reduce((sum, source) => sum + source.points, 0));

    return {
      ...row,
      sheetCount: selectedSheets.length,
      occurrenceRate: selectedSheets.length ? row.appearances / selectedSheets.length : 0,
      averagePointsPerSheet: selectedSheets.length ? row.totalPoints / selectedSheets.length : 0,
      averagePointsWhenPresent: row.appearances ? row.totalPoints / row.appearances : 0,
      minimumPointsPerSheet: pointsBySheet.length ? Math.min(...pointsBySheet) : 0,
      maximumPointsPerSheet: pointsBySheet.length ? Math.max(...pointsBySheet) : 0,
    };
  }).sort((a, b) => (
    b.averagePointsPerSheet - a.averagePointsPerSheet
    || b.occurrenceRate - a.occurrenceRate
    || a.code3.localeCompare(b.code3, "pl", { numeric: true })
  ));
}

export function buildMaturaCourseAdminStats(
  courseStructure,
  sheets,
  sessions = DEFAULT_MATURA_SESSION_FILTER
) {
  const selectedSheets = filterOfficialMaturaSheets(sheets, sessions);
  const frequencyByCode = new Map(
    analyzeMaturaTopicFrequency(selectedSheets, sessions).map((row) => [row.code3, row])
  );

  return Object.fromEntries((Array.isArray(courseStructure) ? courseStructure : [])
    .flatMap((section) => Array.isArray(section?.topics) ? section.topics : [])
    .map((topic) => {
      const sourceCodes = Array.isArray(topic.sourceCodes) && topic.sourceCodes.length
        ? topic.sourceCodes
        : [topic.code];
      const sheetIds = new Set(sourceCodes.flatMap((code) => (
        frequencyByCode.get(code)?.sources?.map((source) => source.sheetId) || []
      )));
      return [topic.code, {
        generatorCount: new Set((topic.taskTypes || []).filter(Boolean)).size,
        examCount: sheetIds.size,
        examTotal: selectedSheets.length,
      }];
    }));
}

export function buildMaturaCoursePriorityRows(
  courseStructure,
  sheets,
  sessions = DEFAULT_MATURA_SESSION_FILTER
) {
  const selectedSheets = filterOfficialMaturaSheets(sheets, sessions);
  const sourceRows = new Map(
    analyzeMaturaTopicFrequency(selectedSheets, sessions).map((row) => [row.code3, row])
  );

  return (Array.isArray(courseStructure) ? courseStructure : [])
    .flatMap((section) => Array.isArray(section?.topics) ? section.topics : [])
    .map((topic) => {
      const codes = Array.isArray(topic.sourceCodes) && topic.sourceCodes.length
        ? topic.sourceCodes
        : [topic.code];
      const rows = codes.map((code) => sourceRows.get(code)).filter(Boolean);
      const pointsBySheet = selectedSheets.map((sheet) => rows.reduce((sum, row) => (
        sum + row.sources
          .filter((source) => source.sheetId === sheet.id)
          .reduce((sourceSum, source) => sourceSum + source.points, 0)
      ), 0));
      const sources = rows.flatMap((row) => row.sources || []);
      const sheetIds = new Set(sources.map((source) => source.sheetId));
      const totalPoints = pointsBySheet.reduce((sum, value) => sum + value, 0);

      return {
        code3: topic.code,
        title: topic.title,
        appearances: sheetIds.size,
        totalPoints,
        closedPoints: rows.reduce((sum, row) => sum + row.closedPoints, 0),
        openPoints: rows.reduce((sum, row) => sum + row.openPoints, 0),
        tasks: rows.reduce((sum, row) => sum + row.tasks, 0),
        sources,
        sheetCount: selectedSheets.length,
        occurrenceRate: selectedSheets.length ? sheetIds.size / selectedSheets.length : 0,
        averagePointsPerSheet: selectedSheets.length ? totalPoints / selectedSheets.length : 0,
        averagePointsWhenPresent: sheetIds.size ? totalPoints / sheetIds.size : 0,
        minimumPointsPerSheet: pointsBySheet.length ? Math.min(...pointsBySheet) : 0,
        maximumPointsPerSheet: pointsBySheet.length ? Math.max(...pointsBySheet) : 0,
      };
    });
}

export function buildMaturaCourseTargetCodes(courseStructure, sheets, targetPercent) {
  if (!Array.isArray(courseStructure) || !Array.isArray(sheets)) return new Set();
  const requestedTarget = Math.min(100, Math.max(0, Number(targetPercent) || 0));
  return new Set(courseStructure
    .flatMap((section) => Array.isArray(section?.topics) ? section.topics : [])
    .filter((topic) => (Number(topic.minimumTarget) || 100) <= requestedTarget)
    .map((topic) => topic.code));
}

export function calculateMaturaCourseCoverage(
  courseStructure,
  sheets,
  selectedCodes,
  sessions = DEFAULT_MATURA_SESSION_FILTER
) {
  const selectedSheets = filterOfficialMaturaSheets(sheets, sessions);
  const selected = selectedCodes instanceof Set ? selectedCodes : new Set(selectedCodes || []);
  const rows = buildMaturaCoursePriorityRows(courseStructure, selectedSheets, sessions)
    .filter((row) => selected.has(row.code3));
  const sheetResults = selectedSheets.map((sheet) => {
    const points = rows.reduce((sum, row) => sum + row.sources
      .filter((source) => source.sheetId === sheet.id)
      .reduce((sourceSum, source) => sourceSum + source.points, 0), 0);
    return {
      sheetId: sheet.id,
      points,
      maxPoints: sheet.maxPoints,
      percent: sheet.maxPoints ? points / sheet.maxPoints * 100 : 0,
    };
  });
  const points = sheetResults.map((result) => result.points);
  const averagePoints = points.length
    ? points.reduce((sum, value) => sum + value, 0) / points.length
    : 0;
  const averageMaxPoints = sheetResults.length
    ? sheetResults.reduce((sum, result) => sum + result.maxPoints, 0) / sheetResults.length
    : 0;

  return {
    sheetCount: sheetResults.length,
    averagePoints,
    averagePercent: averageMaxPoints ? averagePoints / averageMaxPoints * 100 : 0,
    minimumPoints: points.length ? Math.min(...points) : 0,
    maximumPoints: points.length ? Math.max(...points) : 0,
    sheetResults,
  };
}

export function calculateMaturaCourseMasteryCoverage(
  courseStructure,
  sheets,
  selectedCodes,
  masteryByCode,
  sessions = DEFAULT_MATURA_SESSION_FILTER
) {
  const selectedSheets = filterOfficialMaturaSheets(sheets, sessions);
  const selected = selectedCodes instanceof Set ? selectedCodes : new Set(selectedCodes || []);
  const mastery = masteryByCode || {};
  const rows = buildMaturaCoursePriorityRows(courseStructure, selectedSheets, sessions)
    .filter((row) => selected.has(row.code3));
  const sheetResults = selectedSheets.map((sheet) => {
    const points = rows.reduce((sum, row) => {
      const ratio = Math.min(1, Math.max(0, Number(mastery[row.code3]) || 0));
      const availablePoints = row.sources
        .filter((source) => source.sheetId === sheet.id)
        .reduce((sourceSum, source) => sourceSum + source.points, 0);
      return sum + availablePoints * ratio;
    }, 0);
    return {
      sheetId: sheet.id,
      points,
      maxPoints: sheet.maxPoints,
      percent: sheet.maxPoints ? points / sheet.maxPoints * 100 : 0,
    };
  });
  const points = sheetResults.map((result) => result.points);
  const averagePoints = points.length
    ? points.reduce((sum, value) => sum + value, 0) / points.length
    : 0;
  const averageMaxPoints = sheetResults.length
    ? sheetResults.reduce((sum, result) => sum + result.maxPoints, 0) / sheetResults.length
    : 0;

  return {
    sheetCount: sheetResults.length,
    averagePoints,
    averagePercent: averageMaxPoints ? averagePoints / averageMaxPoints * 100 : 0,
    minimumPoints: points.length ? Math.min(...points) : 0,
    maximumPoints: points.length ? Math.max(...points) : 0,
    sheetResults,
  };
}

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const normalizeMastery = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return clamp(numeric > 1 ? numeric / 100 : numeric, 0, 1);
};

export function calculateMaturaLearningForecast({
  courseStructure,
  sheets,
  quizProgressByCode = {},
  sectionTestProgressBySection = {},
  examTopicResults = [],
  examPerformancePercent = null,
  examAttempts = 0,
  maxPoints = 50,
}) {
  const sections = Array.isArray(courseStructure) ? courseStructure : [];
  const topics = sections.flatMap((section) =>
    (Array.isArray(section?.topics) ? section.topics : [])
      .map((topic) => ({ ...topic, sectionNo: section.sectionNo }))
  );
  const examByCode = new Map(
    (Array.isArray(examTopicResults) ? examTopicResults : [])
      .filter((result) => result?.code)
      .map((result) => [result.code, result])
  );
  const masteryByCode = {};
  let quizAttempts = 0;
  let topicsWithEvidence = 0;
  const sectionTestAttempts = sections.reduce((sum, section) => {
    const progress = sectionTestProgressBySection?.[section.sectionNo];
    const score = normalizeMastery(progress?.lastScore ?? progress?.score);
    const bestScore = normalizeMastery(progress?.bestScore ?? progress?.score);
    return sum + Math.max(
      0,
      Number(progress?.attempts) || (score !== null || bestScore !== null ? 1 : 0)
    );
  }, 0);

  topics.forEach((topic) => {
    const quiz = quizProgressByCode?.[topic.code] || null;
    const lastQuiz = normalizeMastery(quiz?.lastScore ?? quiz?.score);
    const bestQuiz = normalizeMastery(quiz?.bestScore ?? quiz?.score);
    const topicQuizAttempts = Math.max(
      0,
      Number(quiz?.attempts) || (lastQuiz !== null || bestQuiz !== null ? 1 : 0)
    );
    const quizMastery = lastQuiz === null && bestQuiz === null
      ? null
      : (lastQuiz ?? bestQuiz) * 0.7 + (bestQuiz ?? lastQuiz) * 0.3;
    const sectionTest = sectionTestProgressBySection?.[topic.sectionNo] || null;
    const lastSectionTest = normalizeMastery(sectionTest?.lastScore ?? sectionTest?.score);
    const bestSectionTest = normalizeMastery(sectionTest?.bestScore ?? sectionTest?.score);
    const topicSectionTestAttempts = Math.max(
      0,
      Number(sectionTest?.attempts)
        || (lastSectionTest !== null || bestSectionTest !== null ? 1 : 0)
    );
    const sectionTestMastery = lastSectionTest === null && bestSectionTest === null
      ? null
      : (lastSectionTest ?? bestSectionTest) * 0.7
        + (bestSectionTest ?? lastSectionTest) * 0.3;
    const exam = examByCode.get(topic.code);
    const examAvailable = Math.max(0, Number(exam?.availablePoints) || 0);
    const examMastery = examAvailable > 0
      ? clamp(1 - (Number(exam?.missedPoints) || 0) / examAvailable, 0, 1)
      : null;
    const examWeight = examMastery === null
      ? 0
      : Math.min(3, Math.max(1, Number(exam?.attemptsSeen) || 1)) * 2;
    const quizWeight = quizMastery === null
      ? 0
      : Math.min(3, Math.max(1, topicQuizAttempts)) * 0.75;
    const sectionTestWeight = sectionTestMastery === null
      ? 0
      : Math.min(3, Math.max(1, topicSectionTestAttempts)) * 1.25;
    const evidenceWeight = examWeight + sectionTestWeight + quizWeight;

    quizAttempts += topicQuizAttempts;
    if (evidenceWeight > 0) {
      masteryByCode[topic.code] = (
        (examMastery || 0) * examWeight
        + (sectionTestMastery || 0) * sectionTestWeight
        + (quizMastery || 0) * quizWeight
      ) / evidenceWeight;
      topicsWithEvidence += 1;
    } else {
      masteryByCode[topic.code] = 0;
    }
  });

  const normalizedExamPerformance = normalizeMastery(examPerformancePercent);
  const hasExamEvidence = Number(examAttempts) > 0 && normalizedExamPerformance !== null;
  const hasEvidence = hasExamEvidence || sectionTestAttempts > 0 || quizAttempts > 0;
  if (!hasEvidence) {
    return {
      available: false,
      examAttempts: 0,
      quizAttempts: 0,
      sectionTestAttempts: 0,
      topicsWithEvidence: 0,
    };
  }

  const allCodes = new Set(topics.map((topic) => topic.code));
  const topicProjection = calculateMaturaCourseMasteryCoverage(
    courseStructure,
    sheets,
    allCodes,
    masteryByCode
  );
  const topicPoints = clamp(topicProjection.averagePoints, 0, maxPoints);
  const observedExamPoints = hasExamEvidence
    ? normalizedExamPerformance * maxPoints
    : topicPoints;
  const examWeight = hasExamEvidence ? Math.min(0.72, Number(examAttempts) * 0.24) : 0;
  const realisticPoints = topicPoints * (1 - examWeight) + observedExamPoints * examWeight;
  const evidenceUnits = Number(examAttempts) * 2.5
    + Math.min(8, sectionTestAttempts) * 1.25
    + Math.min(12, quizAttempts) * 0.35;
  const confidence = clamp(1 - Math.exp(-evidenceUnits / 7), 0, 0.9);
  const sheetSpread = Math.max(0, topicProjection.maximumPoints - topicProjection.minimumPoints);
  const signalDifference = Math.abs(topicPoints - observedExamPoints);
  const margin = clamp(
    9 - confidence * 6 + sheetSpread * 0.2 + signalDifference * 0.15,
    2.5,
    10
  );
  const toWholePoints = (value) => Math.round(clamp(value, 0, maxPoints));
  const pessimisticPoints = toWholePoints(realisticPoints - margin);
  const expectedPoints = toWholePoints(realisticPoints);
  const optimisticPoints = toWholePoints(realisticPoints + margin);
  const toPercent = (points) => Math.round(points / maxPoints * 100);

  return {
    available: true,
    pessimisticPoints,
    realisticPoints: expectedPoints,
    optimisticPoints,
    pessimisticPercent: toPercent(pessimisticPoints),
    realisticPercent: toPercent(expectedPoints),
    optimisticPercent: toPercent(optimisticPoints),
    confidence,
    confidenceLabel: confidence < 0.38 ? "wstępna" : confidence < 0.66 ? "rosnąca" : "lepsza",
    examAttempts: Number(examAttempts) || 0,
    quizAttempts,
    sectionTestAttempts,
    topicsWithEvidence,
  };
}

export function buildMaturaTargetScenarios(targetPercent, maxPoints = 50) {
  const targetPoints = maxPoints * targetPercent / 100;
  return MATURA_REALIZATION_SCENARIOS.map((scenario) => ({
    ...scenario,
    targetPercent,
    targetPoints,
    projectedPoints: targetPoints * scenario.ratio,
    projectedPercent: targetPercent * scenario.ratio,
  }));
}

export function getRequiredPlanPercent(targetPercent, realizationRatio = 2 / 3) {
  if (!Number.isFinite(targetPercent) || !Number.isFinite(realizationRatio) || realizationRatio <= 0) return null;
  return Math.min(100, targetPercent / realizationRatio);
}

export function buildRecommendedTopicCodes(rows, targetPercent, maxPoints = 50) {
  const targetPoints = maxPoints * Math.min(100, Math.max(0, targetPercent)) / 100;
  const selected = [];
  let points = 0;

  for (const row of Array.isArray(rows) ? rows : []) {
    if (points >= targetPoints && targetPercent < 100) break;
    selected.push(row.code3);
    points += Number(row.averagePointsPerSheet) || 0;
  }

  return selected;
}

export function includeMissingMaturaTopics(rows, topicCodes, sheetCount = 0) {
  const existing = new Map((Array.isArray(rows) ? rows : []).map((row) => [row.code3, row]));

  return (Array.isArray(topicCodes) ? topicCodes : []).map((code3) => existing.get(code3) || {
    code3,
    appearances: 0,
    totalPoints: 0,
    closedPoints: 0,
    openPoints: 0,
    tasks: 0,
    sources: [],
    sheetCount,
    occurrenceRate: 0,
    averagePointsPerSheet: 0,
    averagePointsWhenPresent: 0,
    minimumPointsPerSheet: 0,
    maximumPointsPerSheet: 0,
  });
}

export function calculateTopicPlanProjection(rows, selectedCodes, maxPoints = 50) {
  const selected = selectedCodes instanceof Set ? selectedCodes : new Set(selectedCodes || []);
  const potential = (Array.isArray(rows) ? rows : []).reduce((sum, row) => (
    selected.has(row.code3) ? sum + (Number(row.averagePointsPerSheet) || 0) : sum
  ), 0);
  const toResult = (id, label, ratio) => {
    const projectedPoints = Math.min(maxPoints, Math.round(potential * ratio));
    return {
      id,
      label,
      projectedPoints,
      projectedPercent: Math.round(projectedPoints / maxPoints * 100),
    };
  };

  return [
    toResult("full", "pełna realizacja", 1),
    toResult("expected", "realistycznie", 0.8),
    toResult("conservative", "ostrożnie", 2 / 3),
  ];
}
